import { useCallback, useEffect, useId, useRef, useState } from "react";
import type {
  AnyPeaksSearchRequest,
  AnyPeaksSearchResponse,
} from "@/types/referenceCompound";

/**
 * Any Peaks Search — m/z 로 참조 스펙트럼을 역검색한다 (S5 · 재설계 2026-08-20).
 *
 * 원본(NIST MS Search) Any Peaks 대화상자(기획 v2 §1.4)의 구조를 따른다:
 * 입력줄에서 조건을 만들어 [조건 추가]로 **조건표**에 쌓고, 조건표에는
 * **조건별 히트 수가 즉시** 표시된다("조건마다 히트 수가 즉시 표시된다" —
 * 원본 확정 동작). 요약줄이 원본의 Peaks/Hits 누적표에 해당한다.
 *
 * 지켜야 할 것 (기존 원칙 유지):
 * * **검색 범위를 항상 보여준다** — 우리는 실측 보유분(125종)뿐이다.
 * * 결과 없음 ≠ 물질 없음 — 보유하지 않은 물질일 수 있음을 함께 말한다.
 * * 조건 없이 검색하지 않는다. 판정하지 않는다.
 */

/** 백엔드 기본 허용오차와 같은 값 — 화면이 이 사실을 밝혀야 '단독 충족'
 *  수가 정확 일치로 오독되지 않는다 (교차 리뷰). */
const DEFAULT_MZ_TOLERANCE = 0.5;

interface Condition {
  mz: number;
  min: number | null;
  max: number | null;
}

interface AnyPeaksSearchPanelProps {
  onSearch: (request: AnyPeaksSearchRequest) => Promise<AnyPeaksSearchResponse>;
  onHitClick?: (compoundId: number) => void;
}

function toRequest(
  conditions: Condition[],
  peaksRequired: string,
  maxHits?: number,
): AnyPeaksSearchRequest {
  const required = Number.parseInt(peaksRequired, 10);
  return {
    conditions: conditions.map((c) => ({
      mz: c.mz,
      min_relative_intensity: c.min,
      max_relative_intensity: c.max,
    })),
    peaks_required:
      Number.isFinite(required) && required > 0 && required <= conditions.length
        ? required
        : null,
    ...(maxHits ? { max_hits: maxHits } : {}),
  };
}

export default function AnyPeaksSearchPanel({
  onSearch,
  onHitClick,
}: AnyPeaksSearchPanelProps) {
  const fieldId = useId();

  // 입력줄 (원본의 Peak Type / m/z / Intensity from-to 행)
  const [draftMz, setDraftMz] = useState("");
  const [draftMin, setDraftMin] = useState("");
  const [draftMax, setDraftMax] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  // 조건표 (원본의 Type/m/z/From/To/Hits 표)
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [peaksRequired, setPeaksRequired] = useState("");

  // 미리보기 (조건별 Hits + 누적) — 조건 변경 시 자동 갱신
  const [preview, setPreview] = useState<AnyPeaksSearchResponse | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // 최종 결과 (원본의 [Search])
  const [result, setResult] = useState<AnyPeaksSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const previewSeq = useRef(0);

  const addCondition = () => {
    const mz = Number.parseFloat(draftMz);
    if (!Number.isFinite(mz) || mz <= 0) {
      setDraftError("m/z 를 입력하세요 (양수).");
      return;
    }
    const min = draftMin ? Number.parseFloat(draftMin) : null;
    const max = draftMax ? Number.parseFloat(draftMax) : null;
    if (min != null && max != null && min > max) {
      setDraftError("강도 하한이 상한보다 큽니다.");
      return;
    }
    setDraftError(null);
    previewSeq.current += 1;
    setConditions((prev) => [...prev, { mz, min, max }]);
    setDraftMz("");
    setDraftMin("");
    setDraftMax("");
    setResult(null);
  };

  const removeCondition = (index: number) => {
    // 진행 중 미리보기 무효화 — 지운 조건의 수치가 뒤늦게 도착하지 않게
    previewSeq.current += 1;
    setConditions((prev) => prev.filter((_, i) => i !== index));
    setPreview(null);
    setResult(null);
  };

  const clearAll = () => {
    previewSeq.current += 1;
    setConditions([]);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  // '필요 피크 수'가 바뀌면 이전 검색 결과는 다른 기준의 수치다 —
  // 요약줄이 '1개 충족 기준 → (전부 기준의) N종' 같은 거짓 문장이 되지
  // 않도록 결과를 무효화한다 (교차 리뷰).
  const changeRequired = (value: string) => {
    setPeaksRequired(value);
    setResult(null);
  };

  // 조건별 Hits 즉시 표시 — 조건표가 바뀌면 미리보기 조회 (max_hits=1 로 가볍게)
  const refreshPreview = useCallback(async () => {
    if (conditions.length === 0) {
      setPreview(null);
      return;
    }
    const seq = ++previewSeq.current;
    setIsPreviewing(true);
    try {
      const resp = await onSearch(toRequest(conditions, peaksRequired, 1));
      if (previewSeq.current === seq) setPreview(resp);
    } catch {
      if (previewSeq.current === seq) setPreview(null);
    } finally {
      if (previewSeq.current === seq) setIsPreviewing(false);
    }
  }, [conditions, peaksRequired, onSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void refreshPreview();
    }, 350);
    return () => clearTimeout(timer);
  }, [refreshPreview]);

  const handleSearch = async () => {
    if (conditions.length === 0) {
      setError("조건을 하나 이상 추가하세요.");
      setResult(null);
      return;
    }
    setError(null);
    setIsSearching(true);
    try {
      const response = await onSearch(toRequest(conditions, peaksRequired));
      setResult(response);
    } catch {
      setError("검색에 실패했습니다.");
      setResult(null);
    } finally {
      setIsSearching(false);
    }
  };

  const summary = result ?? preview;
  // 화면에 실제로 적용되는 '필요 피크 수' — 비었거나 범위 밖이면 전부(=조건 수)
  const requiredInput = Number.parseInt(peaksRequired, 10);
  const effectiveRequired =
    Number.isFinite(requiredInput) &&
    requiredInput > 0 &&
    requiredInput <= conditions.length
      ? requiredInput
      : conditions.length;

  return (
    <div className="p-4">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">
          m/z 조건 검색 (Any Peaks Search)
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          특정 이온을 가진 참조물질을 찾습니다. 강도는 각 스펙트럼의 base peak
          를 100 으로 둔 상대강도 기준이고, m/z 는 ±{DEFAULT_MZ_TOLERANCE}{" "}
          안에서 같은 이온으로 봅니다.
        </p>
      </div>

      {/* ── 입력줄: 조건을 만들어 표에 쌓는다 (원본 Accept) ── */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label
              htmlFor={`${fieldId}-mz`}
              className="block text-xs font-medium text-gray-600"
            >
              m/z
            </label>
            <input
              id={`${fieldId}-mz`}
              type="number"
              step="0.1"
              min="0"
              value={draftMz}
              onChange={(e) => setDraftMz(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addCondition();
              }}
              className="w-24 rounded border border-gray-300 px-2 py-1.5 text-sm bg-white"
              placeholder="예: 91"
            />
          </div>
          <div>
            <label
              htmlFor={`${fieldId}-min`}
              className="block text-xs font-medium text-gray-600"
            >
              상대강도 (%)
            </label>
            <div className="flex items-center gap-1">
              <input
                id={`${fieldId}-min`}
                type="number"
                min="0"
                max="100"
                value={draftMin}
                onChange={(e) => setDraftMin(e.target.value)}
                className="w-20 rounded border border-gray-300 px-2 py-1.5 text-sm bg-white"
                placeholder="하한"
              />
              <span className="text-xs text-gray-400">~</span>
              <input
                aria-label="상대강도 상한"
                type="number"
                min="0"
                max="100"
                value={draftMax}
                onChange={(e) => setDraftMax(e.target.value)}
                className="w-20 rounded border border-gray-300 px-2 py-1.5 text-sm bg-white"
                placeholder="상한"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={addCondition}
            className="px-3 py-1.5 text-sm rounded bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-medium"
          >
            + 조건 추가
          </button>
          {conditions.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-500 hover:text-red-600"
            >
              모두 지우기
            </button>
          )}
        </div>
        {draftError && (
          <p className="mt-2 text-xs text-amber-800">{draftError}</p>
        )}
      </div>

      {/* ── 조건표: 조건별 히트 수 즉시 표시 (원본 조건표의 Hits 열) ── */}
      {conditions.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded border border-gray-200 max-w-2xl">
          <table className="w-full text-sm table-fixed">
            <caption className="sr-only">검색 조건 목록</caption>
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left w-24">
                  m/z
                </th>
                <th scope="col" className="px-3 py-2 text-left w-28">
                  강도 하한
                </th>
                <th scope="col" className="px-3 py-2 text-left w-28">
                  강도 상한
                </th>
                <th scope="col" className="px-3 py-2 text-right w-28">
                  단독 충족
                </th>
                <th scope="col" className="px-3 py-2 w-16" />
              </tr>
            </thead>
            <tbody>
              {conditions.map((c, index) => (
                <tr
                  key={`${c.mz}-${c.min}-${c.max}-${index}`}
                  className="border-t border-gray-100"
                >
                  <td className="px-3 py-1.5 font-mono text-gray-900">
                    {c.mz}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">
                    {c.min != null ? `${c.min}%` : "-"}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">
                    {c.max != null ? `${c.max}%` : "-"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-gray-800">
                    {isPreviewing
                      ? "…"
                      : (summary?.condition_hits?.[index] ?? "-")}
                    <span className="text-xs text-gray-400"> 종</span>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => removeCondition(index)}
                      className="text-xs text-gray-400 hover:text-red-600"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── 요약줄 + 옵션 + 검색 (원본의 Peaks/Hits 누적 + Peaks required) ── */}
      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-3">
          <div>
            <label
              htmlFor={`${fieldId}-required`}
              className="block text-xs text-gray-600"
            >
              필요 피크 수 (비우면 전부)
            </label>
            <input
              id={`${fieldId}-required`}
              type="number"
              min="1"
              max={conditions.length || undefined}
              value={peaksRequired}
              onChange={(e) => changeRequired(e.target.value)}
              className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
          </div>
          {summary && conditions.length > 0 && (
            <div className="pb-1.5">
              <p className="text-xs text-gray-600">
                조건 {conditions.length}개 중{" "}
                {effectiveRequired === conditions.length
                  ? `전부(${conditions.length})`
                  : `${effectiveRequired}개`}{" "}
                충족 기준 → <strong>{summary.total_hits}종</strong> / 검색 대상{" "}
                {summary.searched_compound_count}종 (실측 스펙트럼 보유분)
              </p>
              {summary.total_hits === 0 &&
                conditions.length > 1 &&
                effectiveRequired > 1 &&
                (summary.condition_hits ?? []).some((n) => n > 0) && (
                  <p className="mt-0.5 text-xs text-gray-500">
                    조건을 모두 충족하는 물질이 없습니다 — 조건별로는 위 표의
                    &lsquo;단독 충족&rsquo;만큼 있습니다. 필요 피크 수를 낮추면
                    일부만 맞는 물질도 나옵니다.
                  </p>
                )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={isSearching || conditions.length === 0}
          className="px-5 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 font-medium"
        >
          {isSearching ? "검색 중…" : "검색"}
        </button>
      </div>

      {error && (
        <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </p>
      )}

      {/* ── 결과 ── */}
      {result && (
        <div className="mt-4">
          {result.hits.length > 0 && (
            <p className="mb-2 text-xs text-gray-600">
              검색 결과{" "}
              {result.truncated
                ? `${result.total_hits}건 중 ${result.hits.length}건 표시`
                : `${result.total_hits}건`}
            </p>
          )}

          {result.hits.length === 0 ? (
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-600">
              <p>조건에 맞는 참조물질이 없습니다.</p>
              <p className="mt-1 text-xs text-gray-500">
                검색 대상은 실측 스펙트럼을 보유한{" "}
                {result.searched_compound_count}종뿐입니다 — 우리가 아직{" "}
                <strong>보유하지 않은 물질일 수 있습니다</strong>.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-200">
              <table className="w-full text-sm">
                <caption className="sr-only">m/z 조건 검색 결과</caption>
                {/* uppercase 금지 — m/z 가 M/Z 로 깨진다 (과학 표기) */}
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">
                      화합물
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      분자식
                    </th>
                    <th scope="col" className="px-3 py-2 text-left">
                      맞은 피크 (m/z · 상대강도)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.hits.map((hit) => (
                    <tr
                      key={hit.compound_id}
                      className={`border-t border-gray-100 ${
                        onHitClick ? "cursor-pointer hover:bg-gray-50" : ""
                      }`}
                      onClick={() => onHitClick?.(hit.compound_id)}
                    >
                      <td className="px-3 py-2 text-gray-900">
                        {hit.compound_name}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-600">
                        {hit.molecular_formula ?? "-"}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        <span className="flex flex-wrap gap-1">
                          {hit.matched_peaks.map((peak) => (
                            <span
                              key={peak.mz}
                              className="inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 font-mono text-indigo-800"
                            >
                              {peak.mz} · {peak.relative_intensity.toFixed(1)}%
                            </span>
                          ))}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
